<template>
    <BaseForm>
        <template #fields>
            <TextAreaField
                :value="values.notesDayOrder"
                :label="$t('notes-dayorder')"
                :error-messages="errors.notesDayOrder"
                :max-length="EventSeriesDayOrder.shape.notesDayOrder.maxLength ?? undefined"
                @input="setFieldValue('notesDayOrder', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesDayOrder', true)"
            />
            <TextAreaField
                :value="values.notesSetup"
                :label="$t('notes-setup')"
                :error-messages="errors.notesSetup"
                :max-length="EventSeriesDayOrder.shape.notesSetup.maxLength ?? undefined"
                @input="setFieldValue('notesSetup', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesSetup', true)"
            />
            <TextAreaField
                :value="values.notesEarlyConstructionInternal"
                :label="$t('notes-early-construction-internal')"
                :error-messages="errors.notesEarlyConstructionInternal"
                :max-length="EventSeriesDayOrder.shape.notesEarlyConstructionInternal.maxLength ?? undefined"
                @input="setFieldValue('notesEarlyConstructionInternal', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesEarlyConstructionInternal', true)"
            />
            <TextAreaField
                :value="values.notesEarlyConstructionExternal"
                :label="$t('notes-early-construction-external')"
                :error-messages="errors.notesEarlyConstructionExternal"
                :max-length="EventSeriesDayOrder.shape.notesEarlyConstructionExternal.maxLength ?? undefined"
                @input="setFieldValue('notesEarlyConstructionExternal', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesEarlyConstructionExternal', true)"
            />
            <TextAreaField
                :value="values.notesConstruction"
                :label="$t('notes-construction')"
                :error-messages="errors.notesConstruction"
                :max-length="EventSeriesDayOrder.shape.notesConstruction.maxLength ?? undefined"
                @input="setFieldValue('notesConstruction', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesConstruction', true)"
            />
            <TextAreaField
                :value="values.notesExecution"
                :label="$t('notes-execution')"
                :error-messages="errors.notesExecution"
                :max-length="EventSeriesDayOrder.shape.notesExecution.maxLength ?? undefined"
                @input="setFieldValue('notesExecution', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesExecution', true)"
            />
            <TextAreaField
                :value="values.notesDismantling"
                :label="$t('notes-dismantling')"
                :error-messages="errors.notesDismantling"
                :max-length="EventSeriesDayOrder.shape.notesDismantling.maxLength ?? undefined"
                @input="setFieldValue('notesDismantling', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesDismantling', true)"
            />
            <TextAreaField
                :value="values.notesExtendedDismantlingExternal"
                :label="$t('notes-extended-dismantling-external')"
                :error-messages="errors.notesExtendedDismantlingExternal"
                :max-length="EventSeriesDayOrder.shape.notesExtendedDismantlingExternal.maxLength ?? undefined"
                @input="setFieldValue('notesExtendedDismantlingExternal', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesExtendedDismantlingExternal', true)"
            />
            <TextAreaField
                :value="values.notesExtendedDismantlingInternal"
                :label="$t('notes-extended-dismantling-internal')"
                :error-messages="errors.notesExtendedDismantlingInternal"
                :max-length="EventSeriesDayOrder.shape.notesExtendedDismantlingInternal.maxLength ?? undefined"
                @input="setFieldValue('notesExtendedDismantlingInternal', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesExtendedDismantlingInternal', true)"
            />
            <TextAreaField
                :value="values.notesTechnicalDismantling"
                :label="$t('notes-technical-dismantling')"
                :error-messages="errors.notesTechnicalDismantling"
                :max-length="EventSeriesDayOrder.shape.notesTechnicalDismantling.maxLength ?? undefined"
                @input="setFieldValue('notesTechnicalDismantling', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesTechnicalDismantling', true)"
            />
            <TextAreaField
                :value="values.notesDayOrderHistory"
                :label="$t('notes-dayorder-history')"
                :error-messages="errors.notesDayOrderHistory"
                :help-button-text="$t('notes-dayorder-history-help')"
                :max-length="EventSeriesDayOrder.shape.notesDayOrderHistory.maxLength ?? undefined"
                @input="setFieldValue('notesDayOrderHistory', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesDayOrderHistory', true)"
            />
        </template>
    </BaseForm>
</template>
<script setup lang="ts">
import BaseForm from '@/components/Form/BaseForm.vue';
import TextAreaField from '@/components/Form/TextAreaField.vue';
import { useEventSeriesInjection } from '@/event/composables/useEventSeriesInjection';
import { useForm } from '@/forms/composables/useForm';
import { computed } from 'vue';
import { EventSeriesDayOrder } from '../schemas';

const props = defineProps<{ seriesId: string }>();

const seriesId = computed(() => props.seriesId);
const { series, updateEventSeries } = useEventSeriesInjection();

const { errors, values, setFieldValue, setFieldTouched, validate, onDiscard, resetForm } = useForm(
    EventSeriesDayOrder,
    computed(() => ({
        notesDayOrder: series.value?.notesDayOrder ?? '',
        notesSetup: series.value?.notesSetup ?? '',
        notesEarlyConstructionInternal: series.value?.notesEarlyConstructionInternal ?? '',
        notesEarlyConstructionExternal: series.value?.notesEarlyConstructionExternal ?? '',
        notesConstruction: series.value?.notesConstruction ?? '',
        notesExecution: series.value?.notesExecution ?? '',
        notesDismantling: series.value?.notesDismantling ?? '',
        notesExtendedDismantlingExternal: series.value?.notesExtendedDismantlingExternal ?? '',
        notesExtendedDismantlingInternal: series.value?.notesExtendedDismantlingInternal ?? '',
        notesTechnicalDismantling: series.value?.notesTechnicalDismantling ?? '',
        notesDayOrderHistory: series.value?.notesDayOrderHistory ?? '',
    })),
);

async function save() {
    const valid = await validate();
    if (valid) {
        await updateEventSeries({ id: seriesId.value, ...values.value });
    }
}

defineExpose({ save, onDiscard, resetForm });
</script>
